"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { GeneralSettingsResponse } from "../types/general";
import { fetchGeneralSettings } from "@/api-service/general";

// Define context type
interface GeneralSettingsContextType {
  settings: Omit<
    GeneralSettingsResponse["data"],
    "shipping" | "countryShipping"
  > | null;
  shipping: {
    shipping_settings: number;
    countryShipping: {
      id: number;
      minimumAmount: number;
      maximumAmount: number;
      shippingFee: number;
      countryId: number;
      currencyId?: number;
    }[];
  } | null;
  loading: boolean;
  refreshSettings: () => Promise<void>;
}

// Create Context
const GeneralSettingsContext = createContext<
  GeneralSettingsContextType | undefined
>(undefined);

export const GeneralSettingsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [settings, setSettings] = useState<Omit<
    GeneralSettingsResponse["data"],
    "shipping" | "countryShipping"
  > | null>(null);

  const [shipping, setShipping] = useState<
    GeneralSettingsContextType["shipping"] | null
  >(null);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await fetchGeneralSettings();

      if (response?.status === "SUCCESS") {
        const { shipping, countryShipping, ...rest } = response?.data;

        // split objects
        setSettings(rest);
        setShipping({
          shipping_settings: shipping.shipping_settings,
          countryShipping,
        });

        // Save separately in localStorage
        localStorage.setItem("generalSettings", JSON.stringify(rest));
        localStorage.setItem(
          "shippingSettings",
          JSON.stringify({
            shipping_settings: shipping.shipping_settings,
            countryShipping,
          })
        );
      }
    } catch (error) {
      console.error("Failed to fetch general settings:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      const localGeneral = localStorage.getItem("generalSettings");
      const localShipping = localStorage.getItem("shippingSettings");

      if (localGeneral) {
        try {
          setSettings(JSON.parse(localGeneral));
        } catch (e) {
          console.error("Failed to parse generalSettings from localStorage", e);
        }
      }
      if (localShipping) {
        try {
          setShipping(JSON.parse(localShipping));
        } catch (e) {
          console.error("Failed to parse shippingSettings from localStorage", e);
        }
      }
    }

    fetchSettings();
  }, []);

  return (
    <GeneralSettingsContext.Provider
      value={{ settings, shipping, loading, refreshSettings: fetchSettings }}
    >
      {children}
    </GeneralSettingsContext.Provider>
  );
};

// Hook
export const useGeneralSettings = (): GeneralSettingsContextType => {
  const context = useContext(GeneralSettingsContext);
  if (!context) {
    throw new Error(
      "useGeneralSettings must be used inside GeneralSettingsProvider"
    );
  }
  return context;
};
